import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n";
import { Lightbox } from "../components/Lightbox";

const item = {
  hidden: { opacity: 0, y: 50 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: i * 0.08, ease: [0.19, 1, 0.22, 1] } }),
};

const ORDER = [
  { src: "/images/c2qliukf_11__bis_.webp", alt: "Pergola et terrasse", className: "col-span-2 row-span-2" },
  { src: "/images/f5lbskap_25.webp", alt: "Villa et piscine", className: "col-span-2 row-span-1" },
  { src: "/images/gnvgarcj_23.webp", alt: "Jacuzzi", className: "col-span-1 row-span-1" },
  { src: "/images/6gp8dvje_18.webp", alt: "Coin détente sous voile", className: "col-span-1 row-span-1" },
];

const ALL_PHOTOS = [
  "/images/c2qliukf_11__bis_.webp",
  "/images/6gp8dvje_18.webp",
  "/images/plnvx0j4_28.webp",
  "/images/5liggpuz_32.webp",
  "/images/gnvgarcj_23.webp",
  "/images/fv3vdfas_26-07-03-Villa_LC_Charpente-high_17.webp",
  "/images/ao056apk_31.webp",
  "/images/f5lbskap_25.webp",
  "/images/i8jq2e6i_26-07-03-Villa_LC_Charpente-high_18.webp",
  "/images/ej57g76x_33.webp",
  "/images/oig7z7ge_34.webp",
  "/images/ab14437p_26-07-03-Villa_LC_Charpente-high_11.webp",
  "/images/kadvt7di_17.webp",
  "/images/3aa0858db5133bdebd58167caa91a1617a0c34d84b821356fb731ab5bd07.webp",
  "/images/g2oblwp7_26.webp",
  "/images/l0o39sxg_14.webp",
  "/images/499ocupq_4.webp",
  "/images/w8e4kkua_8.webp",
  "/images/4cnq9dlp_12.webp",
  "/images/6o7quwhr_3.webp",
  "/images/fjsyjs4v_17.webp",
  "/images/kbz5tjhb_15.webp",
  "/images/z0e62mb2_2.webp",
  "/images/2qpgv4zp_20.webp",
  "/images/tb8e2fdi_5.webp",
  "/images/6g9p3zld_7.webp",
  "/images/elh84mll_19.webp",
  "/images/aczsuiik_6.webp",
  "/images/9lzz6uzs_9.webp",
  "/images/dl2m9mrd_40.webp",
  "/images/t7hz82qi_105.webp",
  "/images/earb12rc_113.webp",
  "/images/l9n5exs7_89.webp",
  "/images/ldchv5ae_4.webp",
  "/images/e63gqz1e_5.webp",
  "/images/0u9bctwb_3.webp",
  "/images/p0v34w2v_2.webp",
  "/images/1sa2ence_9.webp",
  "/images/225awlk2_1111.webp",
  "/images/i6ny00wd_6.webp",
  "/images/y9qy6aob_3.webp",
  "/images/wsyovtad_4.webp",
  "/images/gzl56si9_1.webp",
  "/images/sk4lnrf1_10.webp",
  "/images/zej1jfs0_8.webp",
  "/images/ok2zjwoe_5.webp",
  "/images/tvfu7urh_3.webp",
  "/images/vavrxfe8_2.webp",
  "/images/m53m9f5u_7.webp",
  "/images/wdb5t13b_2.webp",
  "/images/uirdipkl_4.webp",
  "/images/pkqrs450_5.webp",
  "/images/bm0abys5_6.webp",
  "/images/r81aewx5_1.webp",
  "/images/rxrl892q_2.webp",
  "/images/rf5ibuxy_6.webp",
  "/images/7s6x48qj_4.webp",
  "/images/ncbq806x_7.webp",
];

export const Gallery = () => {
  const { t } = useLang();
  const [lb, setLb] = useState(null);
  const [lbAll, setLbAll] = useState(null);
  const images = ORDER.map((o) => o.src);

  return (
    <section id="galerie" className="px-6 md:px-12 pt-10 md:pt-16 pb-24 md:pb-36 max-w-[1600px] mx-auto" data-testid="gallery-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 md:mb-20 gap-6">
        <div>
          <span className="text-[11px] tracking-[0.35em] uppercase text-terracotta">{t.gallery.eyebrow}</span>
          <h2 className="font-serif font-light text-4xl md:text-6xl mt-4 tracking-tight">{t.gallery.title}</h2>
        </div>
        <p className="text-ink-soft font-light max-w-xs md:text-right">{t.gallery.hint}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 auto-rows-[24vh] md:auto-rows-[30vh]">
        {ORDER.map((o, i) => (
          <motion.button
            key={o.src}
            custom={i}
            variants={item}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            onClick={() => setLb(i)}
            data-testid={`gallery-tile-${i}`}
            className={`overflow-hidden group relative ${o.className}`}
          >
            <img
              src={o.src}
              alt={o.alt}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/15 transition-colors duration-500" />
          </motion.button>
        ))}
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={() => setLbAll(0)}
          data-testid="gallery-more-btn"
          className="inline-flex items-center gap-3 border border-ink text-ink px-8 py-4 text-sm tracking-wide hover:bg-ink hover:text-sand-bg transition-colors duration-300"
        >
          {t.gallery.more}
        </button>
      </div>

      <Lightbox images={images} index={lb} onClose={() => setLb(null)} onNav={setLb} />
      <Lightbox images={ALL_PHOTOS} index={lbAll} onClose={() => setLbAll(null)} onNav={setLbAll} />
    </section>
  );
};
